// Global error handler that can be set by AurigaContext
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { forEach } from 'lodash';

// Key used to store the timestamp of the last credential-related reload in localStorage
const RELOAD_GUARD_KEY = 'lastCredentialReload';
// Minimum time (ms) between reloads to prevent infinite reload loops when auth infrastructure is unavailable
const RELOAD_COOLDOWN_MS = 10_000; // 10 seconds

/**
 * Safely reloads the page for re-authentication, with loop detection.
 * Preserves the current URL for post-auth redirect.
 * @returns true if reload was triggered, false if loop was detected and reload was skipped.
 */
export const safeReloadForReauth = (): boolean => {
  const lastReload = Number(localStorage.getItem(RELOAD_GUARD_KEY) || 0);
  const now = Date.now();

  if (now - lastReload < RELOAD_COOLDOWN_MS) {
    console.error('Reload loop detected. Aborting auto-reload for re-authentication.');
    return false;
  }

  localStorage.setItem(RELOAD_GUARD_KEY, String(now));
  // Preserve current location so handleRedirectAfterAuth can restore it after re-authentication
  localStorage.setItem('redirectAfterAuth', window.location.href);
  window.location.reload();
  return true;
};

let globalErrorHandler: ((message: string) => void) | null = null;

/**
 * Sets the global error handler function
 */
export const setGlobalErrorHandler = (handler: (message: string) => void) => {
  globalErrorHandler = handler;
};

/**
 * Surface error message(s) from any error type
 * @param error - The error object
 * @returns One or more toasts of Error message(s) from any error type
 */
export const surfaceErrorMessages = (error: unknown) => {
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;

    // Expects that API errors will be one or more GraphQL Errors if we have an array
    if (Array.isArray(errorObj.errors) && errorObj.errors.length > 0) {
      const gqlErrorResult: GraphQLResult = error as GraphQLResult;
      forEach(gqlErrorResult?.errors, (gqlError, idx) => {
        if (idx === 0) console.debug('Received error: ', gqlError); // Log at least the first error to console
        // Toast error message
        if (gqlError?.message) globalErrorHandler?.(gqlError.message);
      });

      // Surface output of any partial data received for debugging
      if (gqlErrorResult?.data) {
        console.debug('Received partial data: ', gqlErrorResult.data);
      }
    } else if (typeof errorObj.message === 'string') {
      // Check for direct message property
      globalErrorHandler?.(errorObj.message);
    } else {
      // Output unknown error to console and surface toast
      console.error('Unknown error:', error);
      globalErrorHandler?.('Unknown error');
    }
  }
};

/**
 * Handles errors globally using the registered handler
 * Used at: src/index.tsx
 */
export const handleGlobalError = (error: unknown) => {
  // Detect expired/missing credentials — when session is dead, force a User re-authentication via reload
  if (error instanceof Error && error.name === 'NoCredentials') {
    console.warn('Session credentials expired. Reloading to re-authenticate.');
    safeReloadForReauth();
    return;
  }

  if (globalErrorHandler) {
    surfaceErrorMessages(error);
  } else {
    // No global handler available, so output the error to the console
    console.error('Global Error:', error);
  }
};
